import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@/lib/api';
import {
  getLearningTargetMinutesConfigApi,
  updateLearningTargetMinutesConfigApi,
} from '@/lib/reports-api';
import {
  canSaveLearningTargetMinutes,
  DEFAULT_LEARNING_TARGET_MINUTES_RANGE,
  isLearningTargetMinutesInRange,
  LEARNING_TARGET_MINUTES_LABEL,
  learningTargetMinutesRangeHint,
  type LearningTargetMinutesRange,
} from '@/lib/learning-target-minutes';
import { Card } from '@/components/ui/card';
import { ErrorState, LoadingState } from '@/components/ui/empty-state';

/**
 * Issue #278 — 管理员「系统设置 → KPI考核 → 目标分钟数」。
 *
 * 真实 GET/PATCH /system/learning-target-minutes-config（GET 任意已登录可读，PATCH 仅 ADMIN+，
 * 按 orgId 隔离）。该目标（#292 口径：默认 5，区间 1-1000）即移动端首页「今日已学 · 目标 N 分钟」
 * 的目标分钟数，保存后运行时即时生效、写审计日志，无需重启服务。
 */
const INPUT =
  'w-28 rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100';
const PRIMARY_BTN =
  'inline-flex items-center gap-2 rounded-md bg-brand-500 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-brand-600 disabled:opacity-60';

export function LearningTargetMinutesSettingsCard({ canEdit }: { canEdit: boolean }) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['system', 'learning-target-minutes-config'],
    queryFn: getLearningTargetMinutesConfigApi,
  });
  const [draft, setDraft] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (query.data) setDraft(String(query.data.targetMinutes));
  }, [query.data]);

  const mutation = useMutation({
    mutationFn: (targetMinutes: number) =>
      updateLearningTargetMinutesConfigApi(targetMinutes),
    onSuccess: async (data) => {
      setSaveError(null);
      setSaved(true);
      setDraft(String(data.targetMinutes));
      await queryClient.invalidateQueries({
        queryKey: ['system', 'learning-target-minutes-config'],
      });
    },
    onError: (err) =>
      setSaveError(
        err instanceof ApiError ? `${err.message}（HTTP ${err.status}）` : '保存失败，请重试。',
      ),
  });

  if (query.isLoading) {
    return (
      <Card title={LEARNING_TARGET_MINUTES_LABEL}>
        <LoadingState label="正在加载目标分钟数…" />
      </Card>
    );
  }
  if (query.isError || draft === null || !query.data) {
    return (
      <Card title={LEARNING_TARGET_MINUTES_LABEL}>
        <ErrorState
          title="加载目标分钟数失败"
          description={query.error instanceof ApiError ? query.error.message : '请稍后重试。'}
          retry={() => void query.refetch()}
        />
      </Card>
    );
  }

  const range: LearningTargetMinutesRange = {
    min: query.data.min ?? DEFAULT_LEARNING_TARGET_MINUTES_RANGE.min,
    max: query.data.max ?? DEFAULT_LEARNING_TARGET_MINUTES_RANGE.max,
    defaultTargetMinutes:
      query.data.defaultTargetMinutes ??
      DEFAULT_LEARNING_TARGET_MINUTES_RANGE.defaultTargetMinutes,
  };
  const localInvalid = !isLearningTargetMinutesInRange(draft, range);
  const canSave = canSaveLearningTargetMinutes({
    canEdit,
    pending: mutation.isPending,
    raw: draft,
    range,
  });

  const onSave = () => {
    setSaved(false);
    setSaveError(null);
    if (!canSave) return;
    mutation.mutate(Number(draft.trim()));
  };

  return (
    <Card
      title={LEARNING_TARGET_MINUTES_LABEL}
      description="员工每日学习目标分钟数。移动端首页「今日已学 · 目标 N 分钟」与目标进度据此渲染，保存后运行时即时生效、写审计日志（无需重启）。默认 5，区间 1-1000。"
    >
      <dl className="mb-4 grid grid-cols-2 gap-y-2 text-sm text-slate-700 sm:grid-cols-3">
        <div>
          <dt className="text-xs uppercase tracking-wide text-slate-500">当前目标</dt>
          <dd className="mt-1 font-medium text-slate-900" data-testid="learning-target-minutes-current">
            {query.data.targetMinutes} 分钟
          </dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wide text-slate-500">允许范围</dt>
          <dd className="mt-1 font-medium text-slate-900">
            {range.min}-{range.max} 分钟
          </dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wide text-slate-500">出厂默认</dt>
          <dd className="mt-1 font-medium text-slate-900">
            {range.defaultTargetMinutes} 分钟
          </dd>
        </div>
      </dl>

      <label className="flex items-center justify-between gap-3 text-sm">
        <span className="text-slate-600">
          {LEARNING_TARGET_MINUTES_LABEL}
          <span className="ml-1 text-xs text-slate-400">
            （{learningTargetMinutesRangeHint(range)}）
          </span>
        </span>
        <input
          className={INPUT}
          type="number"
          inputMode="numeric"
          min={range.min}
          max={range.max}
          step={1}
          value={draft}
          disabled={!canEdit}
          onChange={(e) => {
            setSaved(false);
            setDraft(e.target.value);
          }}
          data-testid="learning-target-minutes-input"
        />
      </label>

      {localInvalid ? (
        <p className="mt-3 text-xs text-amber-600">
          请输入 {range.min}-{range.max} 之间的整数（小于 {range.min} / 大于 {range.max} / 小数将被拒绝）。
        </p>
      ) : null}

      {canEdit ? (
        <div className="mt-4 flex items-center gap-3">
          <button
            className={PRIMARY_BTN}
            onClick={onSave}
            disabled={!canSave}
            data-testid="learning-target-minutes-save"
          >
            {mutation.isPending ? '保存中…' : '保存配置'}
          </button>
          {saved ? (
            <span className="text-xs text-emerald-600">已保存，APP 首页刷新即生效。</span>
          ) : null}
        </div>
      ) : (
        <p className="mt-3 text-xs text-slate-500">仅 ADMIN 及以上角色可修改目标分钟数。</p>
      )}
      {saveError ? (
        <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
          {saveError}
        </div>
      ) : null}
    </Card>
  );
}
